import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Observable, noop, switchMap, tap } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { CustomObjectState } from '../state/state';
import { CustomObjectActions } from '../state/actions';
import { AccountState } from 'app/state/account/state';
import { CustomObjectValue } from '../custom-objects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ProjectChangeTemplateComponent } from 'app/modules/projects/modals/project-change-template/project-change-template.component';

@Component({
  selector: 'app-custom-objects-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomObjectsDashboardComponent {
  @Select(CustomObjectState.getValueById) selectedValue$: Observable<CustomObjectValue>;

  private get customObjectId(): number {
    return +this.activatedRoute.snapshot.params.customObjectId;
  }

  private get customObjectValueId(): number {
    return +this.activatedRoute.snapshot.params.customObjectValueId;
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly store: Store,
    private readonly matDialog: MatDialog,
    private readonly projectService: ProjectsService,
    private readonly activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.store.dispatch(
        new CustomObjectActions.GetValueById({
          id: this.customObjectValueId,
          customObjectId: this.customObjectId,
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this.store.dispatch(new CustomObjectActions.ClearSelectedValue());
  }

  onChangeLayout(selectedValue: CustomObjectValue): void {
    const account = this.store.selectSnapshot(AccountState.getAccount);

    this.projectService.bustProjectTemplatesCache();

    this.projectService
      .getProjectTemplates(Number(account.id))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((projectTemplates) => {
          const options = {
            project_templates: projectTemplates
              .filter(({ related_to_type }) => related_to_type === selectedValue.customObject.name)
              .sort((a, b) => {
                if (a.rank < b.rank) return -1;
                if (a.rank > b.rank) return 1;
                return 0;
              }),
          };

          return this.matDialog
            .open(ProjectChangeTemplateComponent, {
              panelClass: 'mailbox-compose-dialog',
              data: {
                template_id: selectedValue.template?.id,
                options: options,
                related_to_type: selectedValue.customObject.name,
              },
            })
            .afterClosed();
        }),
        tap((result) => {
          if (result?.template_id) {
            this.store.dispatch(
              new CustomObjectActions.UpdateCustomObjectValueTemplateId({
                ...selectedValue,
                template_id: result?.template_id,
              }),
            );
          }
        }),
      )
      .subscribe(noop);
  }
}
